/**
 * Do Not Sell My Personal Information Modal
 * CCPA/CPRA Compliance for California residents
 */

import { useState, useEffect } from 'react';
import { Dialog } from '@headlessui/react';
import { X, ShieldOff, Check, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getConsent, updateConsent } from '../../utils/consentManager';

interface DoNotSellModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function DoNotSellModal({ isOpen, onClose }: DoNotSellModalProps) {
  const [optedOut, setOptedOut] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      const preferences = getConsent();
      // If marketing is disabled, user has already opted out
      setOptedOut(!preferences.marketing);
    }
  }, [isOpen]);

  const handleOptOut = () => {
    setIsSubmitting(true);

    // Disable marketing cookies (which includes Facebook Pixel - the main "selling" activity)
    updateConsent({
      marketing: false
    });

    setTimeout(() => {
      setOptedOut(true);
      setIsSubmitting(false);
    }, 500);
  };

  const handleOptIn = () => {
    setIsSubmitting(true);

    // Re-enable marketing cookies
    updateConsent({
      marketing: true
    });

    setTimeout(() => {
      setOptedOut(false);
      setIsSubmitting(false);
    }, 500);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

      {/* Full-screen container */}
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="mx-auto max-w-2xl w-full bg-white rounded-xl shadow-2xl">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <ShieldOff className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <Dialog.Title className="text-xl font-bold text-gray-900">
                  Do Not Sell or Share My Personal Information
                </Dialog.Title>
                <p className="text-sm text-gray-600 mt-1">
                  CCPA/CPRA Rights for California Residents
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-lg hover:bg-gray-100"
              aria-label="Close dialog"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 space-y-6">
            {/* Current Status */}
            <div className={`rounded-lg p-4 border-2 ${
              optedOut
                ? 'bg-green-50 border-green-200'
                : 'bg-orange-50 border-orange-200'
            }`}>
              <div className="flex items-start gap-3">
                {optedOut ? (
                  <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                ) : (
                  <AlertCircle className="w-6 h-6 text-orange-600 flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <h3 className={`font-semibold ${
                    optedOut ? 'text-green-900' : 'text-orange-900'
                  }`}>
                    {optedOut ? 'You Have Opted Out' : 'You Are Currently Opted In'}
                  </h3>
                  <p className={`text-sm mt-1 ${
                    optedOut ? 'text-green-800' : 'text-orange-800'
                  }`}>
                    {optedOut
                      ? 'Your personal information is not being sold or shared for advertising purposes.'
                      : 'Your personal information may be shared with third parties for advertising purposes.'}
                  </p>
                </div>
              </div>
            </div>

            {/* Explanation */}
            <div className="space-y-4 text-sm text-gray-700">
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  What does "selling" or "sharing" mean under CCPA?
                </h4>
                <p>
                  Under the California Consumer Privacy Act (CCPA) and California Privacy Rights Act (CPRA),
                  "selling" or "sharing" includes disclosing personal information to third parties for
                  cross-context behavioral advertising (targeted ads based on your activity across websites).
                </p>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  What information might be sold or shared?
                </h4>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Your browsing activity on our website (pages visited, time spent)</li>
                  <li>Device information (browser type, operating system)</li>
                  <li>IP address (anonymized)</li>
                  <li>Advertising identifiers and cookies (Facebook Pixel, Click IDs)</li>
                  <li>Interactions with our content and forms</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  Who receives this information?
                </h4>
                <p className="mb-2">
                  Currently, we share data with the following third parties for advertising purposes:
                </p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li><strong>Meta Platforms Inc. (Facebook)</strong> - Facebook Pixel for ad targeting and conversion tracking</li>
                  <li><strong>HubSpot</strong> - Marketing automation and email campaigns</li>
                </ul>
              </div>

              <div>
                <h4 className="font-semibold text-gray-900 mb-2">
                  What happens when I opt out?
                </h4>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Marketing cookies will be disabled immediately</li>
                  <li>Facebook Pixel tracking will stop</li>
                  <li>Your information will no longer be shared for advertising purposes</li>
                  <li>You'll still have full access to our website and services</li>
                  <li>Essential cookies for site functionality will remain active</li>
                  <li>You can opt back in at any time</li>
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-900">
                  <strong>Important:</strong> Opting out will not affect any personal information
                  you provide directly through forms (contact forms, registrations, etc.).
                  We still need that information to provide our services. See our{' '}
                  <Link to="/privacy-policy" className="underline font-medium">
                    Privacy Policy
                  </Link>
                  {' '}for details.
                </p>
              </div>
            </div>
          </div>

          {/* Footer - Actions */}
          <div className="border-t border-gray-200 p-6 bg-gray-50">
            <div className="flex flex-col sm:flex-row gap-3 justify-between">
              <button
                onClick={onClose}
                className="px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
              >
                Close
              </button>

              <div className="flex gap-3">
                {optedOut ? (
                  <button
                    onClick={handleOptIn}
                    disabled={isSubmitting}
                    className="px-6 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary-dark transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Processing...' : 'Opt Back In'}
                  </button>
                ) : (
                  <button
                    onClick={handleOptOut}
                    disabled={isSubmitting}
                    className="px-6 py-2.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? 'Processing...' : 'Opt Out of Sale/Sharing'}
                  </button>
                )}
              </div>
            </div>

            <p className="text-xs text-gray-500 mt-4">
              For questions or to exercise other CCPA rights (access, deletion, correction),
              please email us at{' '}
              <a href="mailto:contact@emploskills.com" className="text-primary hover:underline">
                contact@emploskills.com
              </a>
            </p>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
